import type { LeadForm } from '@/features/lead-forms/api/types';
import { leadFormFieldTypeLabels } from '@/features/lead-forms/api/types';
import type { OrganizationLocation } from '@/features/organization-locations/api/types';
import type { MetaErrorDetail } from '@borradh-workspace/api-client';

/** A branch as the campaign location picker shows it. */
export interface CampaignLocationOption {
  id: string;
  /** The branch name, or its city when it has none. */
  label: string;
  /** Street + city, so two branches with similar names can be told apart. */
  detail: string;
  isPrimary: boolean;
  /**
   * Whether the branch has a real map pin. A radius campaign around a branch
   * without one is refused by the server, so the picker shows it disabled with
   * the fix instead of letting the submit fail.
   */
  targetable: boolean;
}

/**
 * Same ~1km "Null Island" guard the server applies before targeting
 * (`isNearNullIsland` in features/shared/build-meta-targeting): a (0,0) pin is
 * a failed geocode, never a clinic.
 */
const NULL_ISLAND_EPSILON = 0.01;

export function hasMapPin(location: {
  latitude: number | null;
  longitude: number | null;
}): boolean {
  const { latitude, longitude } = location;
  if (latitude == null || longitude == null) return false;
  return !(
    Math.abs(latitude) < NULL_ISLAND_EPSILON &&
    Math.abs(longitude) < NULL_ISLAND_EPSILON
  );
}

export function toCampaignLocationOptions(
  locations: OrganizationLocation[]
): CampaignLocationOption[] {
  return locations.map((location) => ({
    id: location.id,
    label: location.name?.trim() || location.city,
    detail: [location.addressLine1, location.city].filter(Boolean).join(', '),
    isPrimary: location.isPrimary,
    targetable: hasMapPin(location),
  }));
}

/**
 * The branch a new campaign starts on: the primary one when it can be
 * targeted, otherwise the only targetable one. With several candidates and no
 * usable primary the owner picks — never a guess.
 */
export function defaultCampaignLocationId(
  options: CampaignLocationOption[]
): string | undefined {
  const targetable = options.filter((option) => option.targetable);
  const primary = targetable.find((option) => option.isPrimary);
  if (primary) return primary.id;
  return targetable.length === 1 ? targetable[0]?.id : undefined;
}

export async function extractMetaErrorFromResponse(
  error: unknown
): Promise<MetaErrorDetail | undefined> {
  if (
    !error ||
    typeof error !== 'object' ||
    !('response' in error) ||
    !(error as Record<string, unknown>).response
  ) {
    return undefined;
  }

  try {
    const response = (error as { response: Response }).response;
    const body = (await response.clone().json()) as Record<string, unknown>;
    const details = body.details as Record<string, unknown> | undefined;
    if (details?.metaError && typeof details.metaError === 'object') {
      return details.metaError as MetaErrorDetail;
    }
  } catch {
    // Response not JSON or already consumed
  }

  return undefined;
}

export function getCurrencySymbol(currencyCode: string): string {
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
}

export function getLeadFormFieldsPreview(form: LeadForm): string {
  return form.questions
    .map((q) => q.label?.trim() || leadFormFieldTypeLabels[q.type])
    .join(', ');
}
